import { type ReactNode } from 'react'

import { Space, StatusBadge, type StatusBadgeStatus } from '@procurify/ui'
import styled from 'styled-components'

import { configureColumn as col, DataTable } from '@webapp/components/DataTable'
import { type DatatableColumnConfig } from '@webapp/components/DataTable/types/DataTable.types'
import { BridgeLink } from '@webapp/core/components/BridgeLink'
import { PaginationControls } from '@webapp/core/components/PaginationControls'
import { type CurrencyName } from '@webapp/core/entities/external/Currency'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ApprovedExpenseItem } from '@webapp/modules/expense/entities/ApprovedExpenseItem'
import {
  type ExpenseStatusEnum,
  ExpenseStatuses,
} from '@webapp/modules/expense/entities/enums/ExpenseStatusEnum'
import messages from './messages'

// Overrides DataTable's `width: fit-content` cell wrapper so the injected
// account select fills the column (UI-memory "Full-Width Components in
// DataTable Cells").
const FullWidthCellDecoratorStyled = styled.div`
  width: 100%;

  > div {
    display: block;
    width: 100% !important;
  }
`

export interface IApprovedExpenseItemTableProps {
  items: ApprovedExpenseItem[]
  isLoading: boolean
  onEditItem: (item: ApprovedExpenseItem) => void
  renderAccountCell: (item: ApprovedExpenseItem) => ReactNode
  /** RFE link target — React vs Angular route per ARCH_531_MIGRATION_EXPENSE_DETAILS */
  buildExpenseDetailUrl: (uuid: string) => string
  /** Resolves the attachment filename to its media URL (Receipt chip) */
  resolveAttachmentUrl: (attachment: string) => string
  /** Resolves the row's currencyId to a currency code (BHV-012) */
  resolveCurrencyCode: (currencyId: number) => CurrencyName | undefined
  /** Filters popover trigger injected by the Presenter */
  filtersSlot?: ReactNode
}

const getAttachmentFileType = (attachment: string): string =>
  attachment.split('.').pop()?.toUpperCase() ?? ''

export const ApprovedExpenseItemTable = ({
  items,
  isLoading,
  onEditItem,
  renderAccountCell,
  buildExpenseDetailUrl,
  resolveAttachmentUrl,
  resolveCurrencyCode,
  filtersSlot,
}: IApprovedExpenseItemTableProps) => {
  const intl = useProcurifyIntl()

  const getStatusLabel = (status: ExpenseStatusEnum): string => {
    switch (status) {
      case ExpenseStatuses.PENDING:
        return intl.formatMessage(messages.statusPending)
      case ExpenseStatuses.DENIED:
        return intl.formatMessage(messages.statusDenied)
      case ExpenseStatuses.DRAFT:
        return intl.formatMessage(messages.statusDraft)
      default:
        return intl.formatMessage(messages.statusApproved)
    }
  }

  const getStatusType = (status: ExpenseStatusEnum): StatusBadgeStatus => {
    switch (status) {
      case ExpenseStatuses.PENDING:
        return 'inProgress'
      case ExpenseStatuses.DENIED:
        return 'error'
      case ExpenseStatuses.DRAFT:
        return 'inactive'
      default:
        return 'success'
    }
  }

  const columns: (
    | DatatableColumnConfig<'BASIC', ApprovedExpenseItem>
    | DatatableColumnConfig<'DATE', ApprovedExpenseItem>
    | DatatableColumnConfig<'PRICE', ApprovedExpenseItem>
  )[] = [
    col('BASIC', {
      label: intl.formatMessage(messages.columnExpenseNumber),
      locked: true,
      getter: (item) => (
        <BridgeLink to={buildExpenseDetailUrl(item.expenseReport.uuid)}>
          {item.label}
        </BridgeLink>
      ),
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnPrimaryCategory),
      locked: true,
      getter: (item) => item.primaryCategoryName,
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnSecondaryCategory),
      locked: true,
      getter: (item) => item.secondaryCategoryName,
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnAccountCode),
      locked: true,
      width: 220,
      getter: (item) => renderAccountCell(item),
      decorate: (cell: ReactNode) => (
        <FullWidthCellDecoratorStyled>{cell}</FullWidthCellDecoratorStyled>
      ),
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnReceipt),
      getter: (item) =>
        item.attachment ? (
          <a
            href={resolveAttachmentUrl(item.attachment)}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={intl.formatMessage(messages.receiptLinkAriaLabel, {
              label: item.label,
            })}
          >
            {getAttachmentFileType(item.attachment)}
          </a>
        ) : null,
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnType),
      defaultVisible: false,
      getter: (item) => item.expenseTypeLabel,
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnVendor),
      getter: (item) => item.merchant,
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnRequester),
      getter: (item) => item.requesterFullName,
    }),
    col('DATE', {
      label: intl.formatMessage(messages.columnSubmittedDate),
      getter: (item) => ({ date: item.submittedDate }),
    }),
    col('DATE', {
      label: intl.formatMessage(messages.columnApprovedDate),
      defaultVisible: false,
      getter: (item) => ({ date: item.approvedDate }),
    }),
    col('PRICE', {
      label: intl.formatMessage(messages.columnTotalAmount),
      getter: (item) => ({
        price: item.amount,
        currencyName: resolveCurrencyCode(item.currencyId),
      }),
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnComment),
      defaultVisible: false,
      getter: (item) => item.notes,
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnExpensePurpose),
      defaultVisible: false,
      getter: (item) => item.expenseReport.notes,
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnStatus),
      getter: (item) => (
        <StatusBadge
          type={getStatusType(item.expenseReport.status)}
          text={getStatusLabel(item.expenseReport.status)}
        />
      ),
    }),
  ]

  return (
    <PaginationControls id='approved-expense-items'>
      <Space size='large'>
        <Space direction='horizontal' justify='space-between' align='center'>
          <Space direction='horizontal' align='center'>
            <Space.Item minWidth={320}>
              <PaginationControls.Search
                placeholder={intl.formatMessage(messages.searchPlaceholder)}
              />
            </Space.Item>
            {filtersSlot}
            <Space.Item minWidth={200}>
              <PaginationControls.Sort
                sortKeys={[
                  {
                    label: intl.formatMessage(messages.sortReportNumberLabel),
                    value: 'expenseReport',
                  },
                  {
                    label: intl.formatMessage(messages.sortApprovedDateLabel),
                    value: 'approved_datetime',
                  },
                  {
                    label: intl.formatMessage(messages.sortSubmittedDateLabel),
                    value: 'expenseReport__createdDate',
                  },
                  {
                    label: intl.formatMessage(
                      messages.sortPrimaryCategoryLabel
                    ),
                    value: 'location',
                  },
                ]}
              />
            </Space.Item>
          </Space>
          <PaginationControls.Page />
        </Space>

        <DataTable<ApprovedExpenseItem>
          id='approved-expense-items-table'
          aria-label={intl.formatMessage(messages.tableAriaLabel)}
          data={items}
          isLoading={isLoading}
          columns={columns}
          customizableColumns
          persistCustomizedColumns={false}
          emptyStateText={intl.formatMessage(messages.emptyStateText)}
          emptyStateSize='small'
          menuListItems={(item: ApprovedExpenseItem) => [
            {
              icon: 'edit' as const,
              text: intl.formatMessage(messages.editAction),
              onClick: () => onEditItem(item),
            },
          ]}
          hoverable
        />

        <Space direction='horizontal' justify='space-between' align='center'>
          <PaginationControls.PageLimit />
          <PaginationControls.Page />
        </Space>
      </Space>
    </PaginationControls>
  )
}

ApprovedExpenseItemTable.displayName = 'ApprovedExpenseItemTable'
